import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RotateCw } from 'lucide-react';
import { computeSubtitleLayerTextLayout } from '@/lib/subtitles/subtitleEditorLayout';
import { normalizedLayerToPixelRect } from '@/lib/subtitles/subtitleCoordinates';
import {
  dragSubtitleLayer,
  handlePositionStyle,
  normalizeRotationSigned,
  pointerAngleFromCenter,
  resizeSubtitleLayerFromHandle,
  rotationFromPointerDelta,
  SUBTITLE_RESIZE_HANDLES,
} from '@/lib/subtitles/subtitleTransform';
import { useRafThrottle } from '@/hooks/useRafThrottle';
import {
  subtitleAnimationClassName,
  subtitleTextContainerStyle,
} from '@/lib/subtitles/subtitleStyleConfig';
import { CaptionLines } from '@/lib/captions/captionLines';

const HANDLE_HIT_CLASS =
  'absolute z-[60] rounded-sm border border-white/90 bg-sky-400 shadow-md hover:bg-sky-300 touch-none pointer-events-auto';
const HANDLE_SIZE = 14;
const HANDLE_INSET = -7;

/**
 * CapCut-style bounding box: drag, 8-way resize, rotate, inline edit.
 */
export default function SubtitleLayerEditor({
  layer,
  style,
  text = '',
  editText = null,
  tokens = null,
  highlightColor = '#fde047',
  frameWidth,
  frameHeight,
  frameScreenRect,
  aspectRatio,
  viewportMin,
  isActive = true,
  interactive = true,
  snapToGrid = false,
  onLayerChange,
  onTextChange,
  onInteractionStart,
  onInteractionEnd,
}) {
  const [editing, setEditing] = useState(false);
  const [liveLayer, setLiveLayer] = useState(null);
  const liveLayerRef = useRef(null);
  const editRef = useRef(null);
  const sessionRef = useRef(null);

  const sentenceText = editText != null ? String(editText) : String(text || '');
  const displayLayer = liveLayer || layer;
  const fw = Math.max(1, frameWidth);
  const fh = Math.max(1, frameHeight);

  const layout = useMemo(
    () => computeSubtitleLayerTextLayout({
      text,
      layer: displayLayer,
      style,
      frameW: fw,
      frameH: fh,
      aspectRatio,
      viewportMin,
    }),
    [text, displayLayer, style, fw, fh, aspectRatio, viewportMin],
  );

  const pixelRect = layout.rect;

  const { schedule: scheduleLayerPreview, cancel: cancelLayerPreview } = useRafThrottle((next) => {
    liveLayerRef.current = next;
    setLiveLayer(next);
  });

  const endSession = useCallback(() => {
    const session = sessionRef.current;
    sessionRef.current = null;
    if (session) {
      window.removeEventListener('mousemove', session.onMove);
      window.removeEventListener('mouseup', session.onEnd);
      window.removeEventListener('touchmove', session.onTouchMove);
      window.removeEventListener('touchend', session.onEnd);
    }
    cancelLayerPreview();
    const final = liveLayerRef.current;
    liveLayerRef.current = null;
    setLiveLayer(null);
    if (final) onLayerChange?.(final);
    onInteractionEnd?.();
  }, [cancelLayerPreview, onLayerChange, onInteractionEnd]);

  const startSession = useCallback(
    (mode, handle, e) => {
      if (!interactive) return;
      e.preventDefault();
      e.stopPropagation();
      onInteractionStart?.();

      const pt = 'touches' in e ? e.touches[0] : e;
      if (!pt || !frameScreenRect) return;

      const startLayer = { ...layer };
      const startX = pt.clientX;
      const startY = pt.clientY;
      const frameRect = frameScreenRect;

      let rotatePointerStart = null;
      let rotateValueStart = null;
      if (mode === 'rotate') {
        const pr = normalizedLayerToPixelRect(startLayer, fw, fh);
        const cx = frameRect.left + pr.centerX;
        const cy = frameRect.top + pr.centerY;
        rotatePointerStart = pointerAngleFromCenter(startX, startY, cx, cy) + 90;
        rotateValueStart = normalizeRotationSigned(startLayer.rotation || 0);
      }

      const onMove = (ev) => {
        const p = 'touches' in ev ? ev.touches[0] : ev;
        if (!p) return;
        const dx = (p.clientX - startX) / fw;
        const dy = (p.clientY - startY) / fh;
        let next = startLayer;

        if (mode === 'move') {
          next = dragSubtitleLayer(startLayer, dx, dy, aspectRatio, { snap: snapToGrid });
        } else if (mode === 'resize' && handle) {
          next = resizeSubtitleLayerFromHandle(
            handle,
            startLayer,
            dx,
            dy,
            aspectRatio,
            { snap: snapToGrid },
          );
        } else if (mode === 'rotate' && rotatePointerStart != null) {
          const pr = normalizedLayerToPixelRect(startLayer, fw, fh);
          const cx = frameRect.left + pr.centerX;
          const cy = frameRect.top + pr.centerY;
          const rot = rotationFromPointerDelta(
            p.clientX,
            p.clientY,
            cx,
            cy,
            rotatePointerStart,
            rotateValueStart,
          );
          next = { ...startLayer, rotation: rot };
        }

        scheduleLayerPreview(next);
      };

      const onEnd = () => endSession();

      const onTouchMove = (ev) => {
        ev.preventDefault();
        onMove(ev);
      };

      sessionRef.current = { onMove, onEnd, onTouchMove };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onEnd);
      window.addEventListener('touchmove', onTouchMove, { passive: false });
      window.addEventListener('touchend', onEnd);
    },
    [
      interactive,
      layer,
      fw,
      fh,
      frameScreenRect,
      aspectRatio,
      snapToGrid,
      scheduleLayerPreview,
      endSession,
      onInteractionStart,
    ],
  );

  useEffect(() => () => {
    const session = sessionRef.current;
    if (!session) return;
    window.removeEventListener('mousemove', session.onMove);
    window.removeEventListener('mouseup', session.onEnd);
    window.removeEventListener('touchmove', session.onTouchMove);
    window.removeEventListener('touchend', session.onEnd);
    sessionRef.current = null;
  }, []);

  const beginMove = useCallback(
    (e) => {
      if (editing) return;
      if (e.target?.closest?.('[data-subtitle-handle]')) return;
      startSession('move', null, e);
    },
    [editing, startSession],
  );

  const beginResize = useCallback(
    (handle) => (e) => {
      e.preventDefault();
      e.stopPropagation();
      startSession('resize', handle, e);
    },
    [startSession],
  );

  const beginRotate = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      startSession('rotate', null, e);
    },
    [startSession],
  );

  const handleDoubleClick = useCallback(
    (e) => {
      e.stopPropagation();
      if (!interactive || !onTextChange) return;
      setEditing(true);
      requestAnimationFrame(() => {
        const el = editRef.current;
        if (!el) return;
        el.focus();
        const range = document.createRange();
        range.selectNodeContents(el);
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);
      });
    },
    [interactive, onTextChange],
  );

  const finishEdit = useCallback(() => {
    const el = editRef.current;
    const nextText = (el?.innerText || '').trim();
    setEditing(false);
    if (onTextChange && nextText !== sentenceText.trim()) {
      onTextChange(nextText);
    }
  }, [onTextChange, sentenceText]);

  const handleEditKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        if (editRef.current) editRef.current.innerText = sentenceText;
        setEditing(false);
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        finishEdit();
      }
    },
    [finishEdit, sentenceText],
  );

  const opacity = isActive ? 1 : 0.55;
  const rot = normalizeRotationSigned(displayLayer.rotation || 0);
  const animClass = subtitleAnimationClassName(style?.animation);
  const textContainerStyle = subtitleTextContainerStyle(style, layout, text);

  const showHandles = interactive;

  return (
    <div
      className={`absolute touch-none select-none ${interactive ? 'cursor-move pointer-events-auto' : 'pointer-events-none'}`}
      style={{
        left: pixelRect.left,
        top: pixelRect.top,
        width: pixelRect.width,
        height: pixelRect.height,
        transform: `rotate(${rot}deg)`,
        transformOrigin: 'center center',
        opacity,
        zIndex: 20,
        WebkitFontSmoothing: 'antialiased',
      }}
      data-testid="subtitle-layer-editor"
      onMouseDown={interactive && !editing ? beginMove : undefined}
      onTouchStart={interactive && !editing ? beginMove : undefined}
      onDoubleClick={handleDoubleClick}
      title={
        interactive
          ? 'Drag to move · handles to resize · double-click to edit'
          : undefined
      }
    >
      {interactive ? (
        <div
          className={`absolute inset-0 rounded-sm pointer-events-none transition-opacity ${
            isActive ? 'opacity-100' : 'opacity-60'
          }`}
          style={{
            border: isActive ? '1.5px solid rgba(56,189,248,0.95)' : '1px dashed rgba(255,255,255,0.35)',
            boxShadow: isActive ? '0 0 0 1px rgba(0,0,0,0.35)' : 'none',
          }}
          aria-hidden
        />
      ) : null}

      <div
        role="presentation"
        className={`absolute inset-0 z-[10] flex flex-col justify-center overflow-hidden px-1 py-0.5 pointer-events-none ${animClass}`}
        style={textContainerStyle}
      >
        {editing ? (
          <div
            ref={editRef}
            contentEditable
            suppressContentEditableWarning
            className="outline-none w-full min-h-[1em] break-words pointer-events-auto"
            style={{ textAlign: layout.textAlign }}
            onBlur={finishEdit}
            onKeyDown={handleEditKeyDown}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {sentenceText}
          </div>
        ) : layout.lines.length > 0 ? (
          <CaptionLines
            lines={layout.lines}
            tokens={editing ? null : tokens}
            highlightColor={highlightColor}
            animation={style?.animation}
            keyPrefix="subtitle-editor"
          />
        ) : (
          <span className="block min-h-[1em] opacity-40 text-xs">Double-click to edit</span>
        )}
      </div>

      {showHandles
        ? SUBTITLE_RESIZE_HANDLES.map((h) => (
            <button
              key={h}
              type="button"
              aria-label={`Resize ${h}`}
              data-subtitle-handle={h}
              className={HANDLE_HIT_CLASS}
              style={handlePositionStyle(h, HANDLE_INSET, HANDLE_SIZE)}
              onMouseDown={beginResize(h)}
              onTouchStart={beginResize(h)}
            />
          ))
        : null}

      {showHandles ? (
        <button
          type="button"
          aria-label="Rotate subtitle"
          data-subtitle-handle="rotate"
          className={`${HANDLE_HIT_CLASS} z-[60] flex items-center justify-center`}
          style={{
            left: '50%',
            top: -24,
            marginLeft: -HANDLE_SIZE / 2,
            width: HANDLE_SIZE,
            height: HANDLE_SIZE,
            cursor: 'grab',
          }}
          onMouseDown={beginRotate}
          onTouchStart={beginRotate}
        >
          <RotateCw className="w-3 h-3 text-white pointer-events-none" aria-hidden />
        </button>
      ) : null}
    </div>
  );
}
